import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import ShowMoreText from 'react-show-more-text';

import RichText from '../../../Core/RichText';
import imgSuccess from '../../../../images/icon_notification_success.svg';
import imgInfo from '../../../../images/icon_notification_info.svg';
import imgWarning from '../../../../images/icon_notification_warning.svg';
import imgError from '../../../../images/icon_notification_error.svg';
import imgClose from '../../../../images/icon_notification_close.svg';
import imgCloseDark from '../../../../images/icon_notification_close_dark.svg';

import {
  NotificationBarWrapper,
  CustomContainer,
  ContentWrapper,
  CloseBtn,
} from './styles';

const NotificationBanner = ({ data = null, viewed, setViewed, darkMode }) => {
  if (!data) return null;

  const intl = useIntl();

  const { bodyCopyLong, color, id, isDismissible, isFixed, bannerCount } = data;

  // Set the banner view count
  const [count, setCount] = useState(0);

  // Closes the banner
  const closeNotification = () => {
    setViewed(true);
    sessionStorage.setItem(id, true);
  };

  // This sets the state in local storage for the current banner ID
  useEffect(() => {
    const bannerState = { count };
    const currentStorage = JSON.parse(localStorage.getItem(id)) || {
      count: 0,
    };

    if (count >= currentStorage?.count) {
      localStorage.setItem(id, JSON.stringify(bannerState));
    }
  }, [count]);

  // Retrieves the state from localStorage if available and increments the view count
  // If the viewed state is already in session stoarge use that, otherwise set the 'viewed'
  // parameter based on the view count
  useEffect(() => {
    const bannerState = JSON.parse(localStorage.getItem(id));
    let viewedState = JSON.parse(sessionStorage.getItem(id));

    viewedState = !!viewedState;

    if (bannerState) {
      setCount(bannerState.count + 1);

      if (
        bannerState.count > parseInt(bannerCount, 10) &&
        parseInt(bannerCount, 10) !== 0
      ) {
        viewedState = true;
      }
    } else {
      setCount(1);
    }

    setViewed(viewedState);
  }, []);

  // Banner background colors
  const backgroundColors = {
    success: '#DFF5E4',
    info: '#ECF4FE',
    warning: '#FEF7DF',
    error: '#FFEDEC',
  };

  // Banner border colors
  const borderColors = {
    success: '#276600',
    info: '#0A67DC',
    warning: '#FFC700',
    error: '#E1251B',
  };

  // Banner background images (icons)
  const backgroundImages = {
    success: imgSuccess,
    info: imgInfo,
    warning: imgWarning,
    error: imgError,
  };

  return (
    <NotificationBarWrapper
      key={id}
      id={id}
      darkMode={darkMode && darkMode}
      color={color ? backgroundColors[color] : backgroundColors.success}
      borderColor={color ? borderColors[color] : borderColors.success}
      fixed={isFixed}
      isOpen={!viewed}
    >
      <CustomContainer>
        <ContentWrapper
          darkMode={darkMode && darkMode}
          backgroundImage={
            color ? backgroundImages[color] : backgroundImages.success
          }
        >
          <ShowMoreText
            /* Default options */
            lines={2}
            more={`${intl.formatMessage({ id: 'ShowMore' })}`}
            less={`${intl.formatMessage({ id: 'ShowLess' })}`}
            className="notification-css"
            anchorClass="notification-anchor"
            expanded={false}
            width={0}
          >
            {bodyCopyLong && <RichText document={bodyCopyLong} />}
          </ShowMoreText>
          {isDismissible ? (
            <CloseBtn
              type="button"
              onClick={closeNotification}
              aria-label={`${intl.formatMessage({ id: 'close' })}`}
            >
              <img
                src={darkMode ? imgCloseDark : imgClose}
                alt=""
                role="presentation"
              />
            </CloseBtn>
          ) : null}
        </ContentWrapper>
      </CustomContainer>
    </NotificationBarWrapper>
  );
};

NotificationBanner.propTypes = {
  data: PropTypes.shape(),
  viewed: PropTypes.bool.isRequired,
  setViewed: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

export default NotificationBanner;
